import React from 'react'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { useTheme } from '../../../hooks/useTheme'
import { useProjectDetailPage } from '../../../hooks/use-project-detail-page'
import { useProjectLineageMeta } from '../../../hooks/use-project-lineage-meta'
import {
  ProjectDetailEmptyPeriod,
  ProjectDetailFetchError,
  ProjectDetailInvalidId,
  ProjectDetailLoading,
  ProjectDetailNoData,
} from './project-detail-states'
import { ProjectDetailPeriodControls } from './project-detail-period-controls'
import { ProjectDetailHeaderBar } from './project-detail-header-bar'
import { ProjectDetailTabBar } from './project-detail-tab-bar'
import { ProjectDetailOverviewTab } from './project-detail-overview-tab'
import { ProjectDetailResourcesTab } from './project-detail-resources-tab'
import { ProjectDetailMonthlyTab } from './project-detail-monthly-tab'

export const ProjectDetailPage: React.FC = () => {
  const { isDark, mounted } = useTheme()
  const d = useProjectDetailPage()
  const lineage = useProjectLineageMeta(
    Number.isFinite(d.projectId) ? d.projectId : null,
  )

  if (!mounted) return null
  if (!Number.isFinite(d.projectId)) return <ProjectDetailInvalidId />
  if (d.loading) return <ProjectDetailLoading />
  if (d.error) return <ProjectDetailFetchError message={d.error} />

  if (!d.projectStats && d.projectRawData.length > 0) {
    return (
      <ProjectDetailEmptyPeriod
        pageTitle={d.projectDetailName}
        projectId={d.projectId}
        detailMode={d.detailMode}
        onDetailModeChange={d.setDetailMode}
        detailFY={d.detailFY}
        onDetailFYChange={d.setDetailFY}
        detailQ={d.detailQ}
        onDetailQChange={d.setDetailQ}
        detailYear={d.detailYear}
        onDetailYearChange={d.setDetailYear}
        detailMonth={d.detailMonth}
        onDetailMonthChange={d.setDetailMonth}
        detailFYOptions={d.detailFYOptions}
        detailYearOptions={d.detailYearOptions}
      />
    )
  }

  if (!d.projectStats) {
    return (
      <ProjectDetailNoData
        projectId={d.projectId}
        detailMode={d.detailMode}
        onDetailModeChange={d.setDetailMode}
        detailFY={d.detailFY}
        onDetailFYChange={d.setDetailFY}
        detailQ={d.detailQ}
        onDetailQChange={d.setDetailQ}
        detailYear={d.detailYear}
        onDetailYearChange={d.setDetailYear}
        detailMonth={d.detailMonth}
        onDetailMonthChange={d.setDetailMonth}
        detailFYOptions={d.detailFYOptions}
        detailYearOptions={d.detailYearOptions}
      />
    )
  }

  const s = d.projectStats
  const info = s.projectInfo

  const periodSlot = (
    <ProjectDetailPeriodControls
      detailMode={d.detailMode}
      onDetailModeChange={d.setDetailMode}
      detailFY={d.detailFY}
      onDetailFYChange={d.setDetailFY}
      detailQ={d.detailQ}
      onDetailQChange={d.setDetailQ}
      detailYear={d.detailYear}
      onDetailYearChange={d.setDetailYear}
      detailMonth={d.detailMonth}
      onDetailMonthChange={d.setDetailMonth}
      detailFYOptions={d.detailFYOptions}
      detailYearOptions={d.detailYearOptions}
      className="w-full lg:w-auto justify-end"
    />
  )

  return (
    <AppLayout>
      <PageHeader>
        <ProjectDetailHeaderBar
          title={info.project_name || `Project ${info.project_id}`}
          category={info.project_category}
          lineageMeta={{
            accountManager: lineage.accountManager,
            clientName: lineage.clientName,
            projectManager: lineage.projectManager,
          }}
          lineageMetaLoading={lineage.loading}
          periodSlot={periodSlot}
        />
      </PageHeader>

      <div className="p-6">
        <div className="space-y-3">
          <ProjectDetailTabBar
            activeTab={d.activeTab}
            onTabChange={d.setActiveTab}
          />

          {d.activeTab === 'overview' && (
            <div className="space-y-3">
              <ProjectDetailOverviewTab stats={s} />
              <ProjectDetailMonthlyTab
                stats={s}
                projectDetailName={d.projectDetailName}
                isDark={isDark}
                monthlyChartData={d.monthlyChartData}
              />
            </div>
          )}
          {d.activeTab === 'resources' && (
            <ProjectDetailResourcesTab
              stats={s}
              projectId={d.projectId}
              projectDetailName={d.projectDetailName}
              expandedResourceId={d.expandedResourceId}
              onToggleResource={(uid) =>
                d.setExpandedResourceId(d.expandedResourceId === uid ? null : uid)
              }
            />
          )}
        </div>
      </div>
    </AppLayout>
  )
}

export default ProjectDetailPage
